import { ReactNode, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import MicIcon from '@mui/icons-material/Mic';
import CloudIcon from '@mui/icons-material/Cloud';
import RouterIcon from '@mui/icons-material/Router';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RuleIcon from '@mui/icons-material/Rule';
import SettingsIcon from '@mui/icons-material/Settings';
import { formatCurrency } from '../../utils/formatting';
import { Workflow } from '../../types';
import { WorkflowDetails } from './WorkflowDetails';

// Schema-driven icons resolved by name; unknown names fall back to Settings.
const ICON_REGISTRY: Record<string, ReactNode> = {
  people: <PeopleIcon />,
  chat: <ChatIcon />,
  mic: <MicIcon />,
  cloud: <CloudIcon />,
  router: <RouterIcon />,
  timeline: <TimelineIcon />,
  groups: <GroupsIcon />,
  notifications: <NotificationsIcon />,
  rule: <RuleIcon />,
  cog: <SettingsIcon />,
  settings: <SettingsIcon />,
};

interface UsageConfigSectionProps {
  /** Usage configuration identifier (matches ProjectSchema.calculator.sections[].workflow_name) */
  configName: string;
  /** Icon name from schema, resolved via ICON_REGISTRY */
  icon?: string;
  /** Display title */
  title: string;
  /** Monthly cost for this usage configuration */
  cost: number;
  /** Highlights shown on the card header (cost drivers, summary chips) */
  highlights?: string[];
  /** Workflow-level breakdown for drill-down */
  workflows?: Workflow[];
  /** Whether this config is disabled (e.g., voice disabled) */
  disabled?: boolean;
  /** Whether section starts expanded */
  defaultExpanded?: boolean;
  /** Callback when user clicks "Enable" on a disabled section */
  onEnable?: () => void;
}

export function UsageConfigSection({
  configName: _configName,
  icon: iconName,
  title,
  cost,
  highlights,
  workflows,
  disabled = false,
  defaultExpanded = true,
  onEnable,
}: UsageConfigSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const icon = (iconName && ICON_REGISTRY[iconName]) ?? <SettingsIcon />;
  const displayHighlights = highlights ?? [];
  const hasDetails = Boolean(workflows && workflows.length > 0);

  const handleToggle = () => setExpanded((v) => !v);

  return (
    <Paper
      sx={{
        mb: 2,
        opacity: disabled ? 0.6 : 1,
        transition: 'opacity 0.2s',
        overflow: 'hidden',
      }}
      elevation={1}
    >
      {/* Card header: icon + title + highlight pills + cost + expand toggle. */}
      <Box
        onClick={hasDetails ? handleToggle : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          cursor: hasDetails ? 'pointer' : 'default',
          '&:hover': hasDetails ? { bgcolor: 'action.hover' } : {},
        }}
      >
        <Box
          sx={{
            color: disabled ? 'text.disabled' : 'primary.main',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: displayHighlights.length > 0 && !disabled ? 0.75 : 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ color: disabled ? 'text.disabled' : 'text.primary' }}
            >
              {title}
            </Typography>
            {disabled && onEnable && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEnable();
                }}
              >
                Enable
              </Button>
            )}
            {disabled && !onEnable && (
              <Chip label="Disabled" size="small" variant="outlined" />
            )}
          </Box>
          {/* Highlight pills — visible always, whether the card is
              expanded or not. */}
          {!disabled && displayHighlights.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              {displayHighlights.map((h, i) => (
                <Chip
                  key={i}
                  label={h}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            color={disabled ? 'text.disabled' : 'primary.main'}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {formatCurrency(cost)}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.5 }}
            >
              /mo
            </Typography>
          </Typography>
          {hasDetails && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Expanded body: workflow drill-down only. */}
      {hasDetails && (
        <Collapse in={expanded}>
          <Box sx={{ px: 3, pb: 3 }}>
            <WorkflowDetails workflows={workflows!} />
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}

export { UsageConfigSection as WorkflowSection };
